import React from 'react'
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Typography
} from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { ColumnsType } from 'antd/es/table'
import localeRu from 'antd/es/date-picker/locale/ru_RU'
import InputMask from 'react-input-mask'
import { servicesApi } from 'api/services'
import { EServiceStatus, IAdminService, IPageBooking } from 'models/services'
import { StatusDropdown } from 'components/ui/StatusDropdown/StatusDropdown'
import { CardLink } from 'components/ui/CardLink/CardLink'
import { getUserEditPath } from 'routing/utils/getUserEditPath'
import { showServerError } from 'shared/utils/showServerError'
import { BookingSlotPicker } from './BookingSlotPicker/BookingSlotPicker'
import type { Dayjs } from 'dayjs'
import 'dayjs/locale/ru'

interface PoloClubBookingsProps {
  pageSlug: string
}

const formatTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit'
  })

// заголовок группы: «понедельник, 28 июля 2026»
const formatDayHeader = (iso: string) =>
  new Date(iso).toLocaleDateString('ru-RU', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  })

// ключ группировки по локальному календарному дню (не UTC, иначе слоты у полуночи уедут в соседний день)
const dayKey = (iso: string) => {
  const d = new Date(iso)
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

// бронь без предоплаты (CONFIRMED) отображается как «Не оплачена»
const STATUS_TAG: Record<string, { color: string; label: string }> = {
  PAID: { color: 'green', label: 'Оплачена' },
  PENDING: { color: 'gold', label: 'Ожидает оплаты' },
  CONFIRMED: { color: 'orange', label: 'Не оплачена' },
  PAYMENT_ABANDONED: { color: 'volcano', label: 'Отказ оплаты' },
  CANCELED: { color: 'red', label: 'Отменена' }
}

const tagOf = (status: string) =>
  STATUS_TAG[status] ?? { color: 'default', label: status }

// статусы, которые дальше не меняются: отменять уже нечего
const FINAL_STATUSES = ['CANCELED', 'PAYMENT_ABANDONED']

// столько бронь держит слот, пока идёт оплата (PENDING_HOLD_MS в ServiceBooking.service бэкенда)
const PAYMENT_HOLD_MS = 20 * 60 * 1000

const formatCountdown = (ms: number) => {
  const total = Math.ceil(ms / 1000)
  const mm = String(Math.floor(total / 60)).padStart(2, '0')
  const ss = String(total % 60).padStart(2, '0')
  return `${mm}:${ss}`
}

// Сколько у клиента осталось на оплату. Крон переводит запись в «Отказ оплаты»
// в течение минуты после нуля, до этого показываем 00:00
const PaymentCountdown: React.FC<{ createdAt: string }> = ({ createdAt }) => {
  const deadline = new Date(createdAt).getTime() + PAYMENT_HOLD_MS
  const [left, setLeft] = React.useState(() => deadline - Date.now())

  React.useEffect(() => {
    const timer = setInterval(() => setLeft(deadline - Date.now()), 1000)
    return () => clearInterval(timer)
  }, [deadline])

  return <Tag color='gold'>Осталось {formatCountdown(Math.max(left, 0))}</Tag>
}

interface BookingFormValues {
  service_id: string
  name: string
  phone: string
  email?: string
}

export const PoloClubBookings: React.FC<PoloClubBookingsProps> = ({
  pageSlug
}) => {
  const [bookings, setBookings] = React.useState<IPageBooking[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  // по умолчанию видны только сегодняшние и будущие записи
  const [showArchived, setShowArchived] = React.useState(false)
  const [dateRange, setDateRange] = React.useState<
    [Dayjs | null, Dayjs | null] | null
  >(null)
  const [services, setServices] = React.useState<IAdminService[]>([])
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [form] = Form.useForm<BookingFormValues>()
  // выбор даты/времени через слоты услуги (как на вебе)
  const [occupancy, setOccupancy] = React.useState<{
    taken: Set<string>
    booked: Record<string, number>
  }>({ taken: new Set(), booked: {} })
  const [selectedSlot, setSelectedSlot] = React.useState<string | null>(null)
  const [seats, setSeats] = React.useState(1)
  const [slotError, setSlotError] = React.useState<string | null>(null)

  const serviceId = Form.useWatch('service_id', form)
  const selectedService = services.find((s) => s._id === serviceId)

  const getBookings = React.useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await servicesApi.getPageBookings(pageSlug, {
        archived: showArchived,
        from: dateRange?.[0]?.startOf('day').toISOString(),
        to: dateRange?.[1]?.endOf('day').toISOString()
      })
      setBookings(response.data)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }, [pageSlug, showArchived, dateRange])

  // услуги нужны для выбора в модалке ручной записи (только опубликованные)
  const getServices = async () => {
    try {
      const response = await servicesApi.getPageServices(pageSlug)
      setServices(
        response.data.services.filter(
          (s) => s.status === EServiceStatus.PUBLISHED
        )
      )
    } catch (e) {
      showServerError(e)
    }
  }

  React.useEffect(() => {
    getBookings()
  }, [getBookings])

  React.useEffect(() => {
    getServices()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSlug])

  // при смене услуги подтягиваем её занятость и сбрасываем выбранный слот
  React.useEffect(() => {
    setSelectedSlot(null)
    setSeats(1)
    setSlotError(null)
    if (!serviceId) {
      setOccupancy({ taken: new Set(), booked: {} })
      return
    }
    servicesApi
      .getBookedDatetimes(serviceId)
      .then((res) =>
        setOccupancy({
          taken: new Set(res.data.taken),
          booked: res.data.booked || {}
        })
      )
      .catch(() => setOccupancy({ taken: new Set(), booked: {} }))
  }, [serviceId])

  const cancelBooking = async (id: string) => {
    try {
      await servicesApi.cancelBooking(id)
      await getBookings()
    } catch (e) {
      showServerError(e)
    }
  }

  const closeModal = () => {
    setIsModalOpen(false)
    form.resetFields()
    setSelectedSlot(null)
    setSeats(1)
    setSlotError(null)
  }

  const handleCreate = async (values: BookingFormValues) => {
    if (!selectedSlot) {
      setSlotError('Выберите дату и время')
      return
    }
    try {
      setIsSubmitting(true)
      await servicesApi.createBooking({
        service_id: values.service_id,
        name: values.name,
        phone: values.phone,
        email: values.email || undefined,
        datetime: selectedSlot,
        seats
      })
      closeModal()
      await getBookings()
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Отмена с подтверждением: текст зависит от того, была ли бронь оплачена
  const confirmCancel = (booking: IPageBooking) => {
    Modal.confirm({
      title: 'Отменить запись?',
      content:
        booking.status === 'PAID'
          ? 'Оплата будет возвращена клиенту через ЮKassa.'
          : 'Запись будет отменена.',
      okText: 'Отменить запись',
      cancelText: 'Закрыть',
      okButtonProps: { danger: true },
      onOk: () => cancelBooking(booking._id)
    })
  }

  const columns: ColumnsType<IPageBooking> = [
    {
      title: 'Время',
      dataIndex: 'datetime',
      key: 'datetime',
      width: 100,
      render: (_, { datetime }) => formatTime(datetime)
    },
    {
      title: 'Услуга',
      key: 'service',
      render: (_, { service_id }) =>
        typeof service_id === 'object' && service_id ? service_id.name : '—'
    },
    {
      title: 'Клиент',
      dataIndex: 'name',
      key: 'name',
      render: (_, { name, user_id }) =>
        user_id ? <CardLink to={getUserEditPath(user_id)}>{name}</CardLink> : name
    },
    {
      title: 'Телефон',
      dataIndex: 'phone',
      key: 'phone'
    },
    {
      title: 'Мест',
      key: 'seats',
      width: 90,
      render: (_, { seats, service_id }) => {
        const capacity =
          typeof service_id === 'object' && service_id
            ? service_id.capacity
            : undefined
        return capacity && capacity > 1
          ? `${seats ?? 1} из ${capacity}`
          : (seats ?? 1)
      }
    },
    {
      title: 'Сумма',
      dataIndex: 'amount',
      key: 'amount',
      width: 100,
      render: (_, { amount }) => `${amount} ₽`
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 180,
      render: (_, booking) => {
        const t = tagOf(booking.status)
        const status = FINAL_STATUSES.includes(booking.status) ? (
          <Tag color={t.color}>{t.label}</Tag>
        ) : (
          <StatusDropdown
            color={t.color}
            label={t.label}
            items={[
              { key: 'CANCELED', label: <Tag color='red'>Отменена</Tag> }
            ]}
            onClick={(e) => {
              if (e.key === 'CANCELED') confirmCancel(booking)
            }}
          />
        )

        if (booking.status !== 'PENDING' || !booking.createdAt) return status

        return (
          <Space direction='vertical' size={4}>
            {status}
            <PaymentCountdown createdAt={booking.createdAt} />
          </Space>
        )
      }
    }
  ]

  // группы по дням в хронологическом порядке (бэкенд отдаёт записи по возрастанию datetime)
  const groups = React.useMemo(() => {
    const map = new Map<string, IPageBooking[]>()
    for (const b of bookings) {
      const key = dayKey(b.datetime)
      const list = map.get(key)
      if (list) list.push(b)
      else map.set(key, [b])
    }
    return Array.from(map.values())
  }, [bookings])

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 16,
          marginBottom: 16
        }}
      >
        <DatePicker.RangePicker
          value={dateRange}
          size='large'
          format='DD/MM/YYYY'
          allowEmpty={[true, true]}
          locale={{
            ...localeRu,
            lang: {
              ...localeRu.lang,
              rangePlaceholder: ['От', 'До']
            }
          }}
          onChange={(value) => setDateRange(value)}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Checkbox
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          >
            Показать архивные
          </Checkbox>
          <Button
            type='primary'
            size='large'
            icon={<PlusOutlined />}
            onClick={() => setIsModalOpen(true)}
          >
            Добавить запись
          </Button>
        </div>
      </div>

      {isLoading || bookings.length === 0 ? (
        <Table
          scroll={{ x: 900 }}
          pagination={false}
          columns={columns}
          dataSource={bookings}
          loading={isLoading}
          rowKey={(el) => el._id}
          rowClassName='tableRow'
        />
      ) : (
        groups.map((dayBookings) => (
          <div key={dayKey(dayBookings[0].datetime)} style={{ marginBottom: 24 }}>
            <Typography.Title
              level={5}
              style={{ marginBottom: 8, textTransform: 'capitalize' }}
            >
              {formatDayHeader(dayBookings[0].datetime)}
            </Typography.Title>
            <Table
              scroll={{ x: 900 }}
              pagination={false}
              columns={columns}
              dataSource={dayBookings}
              rowKey={(el) => el._id}
              rowClassName='tableRow'
            />
          </div>
        ))
      )}

      <Modal
        title='Добавить запись'
        open={isModalOpen}
        onCancel={closeModal}
        footer={null}
        width={640}
        destroyOnClose
      >
        <Form form={form} layout='vertical' onFinish={handleCreate}>
          <Form.Item
            label='Услуга'
            name='service_id'
            rules={[{ required: true, message: 'Выберите услугу' }]}
          >
            <Select
              placeholder='Выберите услугу'
              options={services.map((s) => ({
                value: s._id,
                label: `${s.name} — ${s.price} ₽`
              }))}
            />
          </Form.Item>

          <Form.Item
            label='Имя клиента'
            name='name'
            rules={[{ required: true, message: 'Укажите имя' }]}
          >
            <Input placeholder='Иван Иванов' />
          </Form.Item>

          <Form.Item
            label='Телефон'
            name='phone'
            rules={[
              { required: true, message: 'Укажите телефон' },
              // min 18 = полная длина заполненной маски «+7 (999) 999-99-99»
              { min: 18, message: 'Введите телефон полностью' }
            ]}
            extra='На телефон придёт SMS о записи. Если у клиента есть аккаунт — запись также появится в его кабинете и придёт push.'
          >
            <InputMask mask='+7 (999) 999-99-99' maskChar={null}>
              {/* @ts-ignore */}
              {() => <Input placeholder='+7 (999) 000-00-00' />}
            </InputMask>
          </Form.Item>

          <Form.Item
            label='Email (необязательно)'
            name='email'
            extra='Для дополнительного уведомления'
          >
            <Input type='email' placeholder='client@example.com' />
          </Form.Item>

          <Form.Item
            label='Дата и время'
            required
            validateStatus={slotError ? 'error' : undefined}
            help={slotError || undefined}
          >
            {serviceId ? (
              <BookingSlotPicker
                key={serviceId}
                service={selectedService}
                booked={occupancy.booked}
                taken={occupancy.taken}
                value={selectedSlot}
                onChange={(iso) => {
                  setSelectedSlot(iso)
                  if (iso) setSlotError(null)
                }}
                seats={seats}
                onSeatsChange={setSeats}
              />
            ) : (
              <div style={{ color: '#9a9a9a' }}>Сначала выберите услугу</div>
            )}
          </Form.Item>

          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              marginTop: 8
            }}
          >
            <Button onClick={closeModal}>Отмена</Button>
            <Button type='primary' htmlType='submit' loading={isSubmitting}>
              Записать без предоплаты
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  )
}
